import { FC, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';

import { RhfSimpleDatePicker } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfSimpleDatePicker';
import { nameofFactory } from 'src/utils/nameofFactory';
import { INoContractContentStep2Props, NoContractForm } from './types';
import { NoContractSectionHeader } from './SectionHeader';
import { SelectDropdown } from '../../contractManager/filter/SelectDropdown';
import { TableChargesContainer } from './TableChargesContainer';
import { TableChargesShipment } from './TableChargesShipment';
import { TableChargesWeight } from './TableChargesWeight';
import { TableChargesPercentage } from './TableChargesPercentage';
import { TableChargesDeclaration } from './TableChargesDeclaration';
import { TableUnrecognizedService } from './TableUnrecognizedService';

const StyledBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color: '#9F9F9F',
  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#9F9F9F',
    fontSize: '0.75rem',
    top: '3px',
    '@media (min-width: 1720px)': {
      fontSize: '16px',
    },
  },
  '& .MuiFormControl-root .MuiSelect-select': {
    fontSize: '13px',
    '@media (min-width: 1720px)': {
      fontSize: '16px',
    },
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: '6px',
    borderColor: '#DFDFDF',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DFDFDF',
  },
});

const chargeCategoryOptions = [
  { value: 'declaration', label: 'Declaration' },
  { value: 'shipment', label: 'Shipment' },
  { value: 'container', label: 'Container' },
  { value: 'weight', label: 'Weight' },
  { value: 'percentage', label: 'Percentage' },
];

const nameofForm = nameofFactory<NoContractForm>();

/**
 * Component for creating the content of step 2 in the no contract flow.
 *
 * @param {INoContractContentStep2Props} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
export const NoContractContentStep2NewService: FC<INoContractContentStep2Props> = (props) => {
  const { isEditable, matchingService, onEditingChargeChange } = props;
  const { watch, setValue } = useFormContext<NoContractForm>();
  const { containerCharges, declarationCharges, shipmentDeclarationCharges, weightCharges, percentageCharges, contractChoice } = watch();

  const [chargeCategory, setChargeCategory] = useState('shipment');
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Set default dates from unmatchedService.estimatedDepartureDate
  useEffect(() => {
    const estimatedDate = matchingService?.unmatchedService?.estimatedDepartureDate;
    if (estimatedDate) {
      // Convert string to Date object and set both start and end dates
      const dateObject = new Date(estimatedDate);
      setValue('newMatchingService.existingShippingService.validityPeriod.start', dateObject);
      setValue('newMatchingService.existingShippingService.validityPeriod.end', dateObject);
    }
  }, [matchingService?.unmatchedService?.estimatedDepartureDate, setValue]);

  // Notify parent when editing state changes
  useEffect(() => {
    if (onEditingChargeChange) {
      onEditingChargeChange(isEditing);
    }
  }, [isEditing, onEditingChargeChange]);

  const handleAddNewCharge = () => {
    // Create new row with necessary fields based on charge category
    switch (chargeCategory) {
      case 'declaration': {
        const newDeclarationRow = {
          chargeName: '',
          chargeCategory: '',
          legType: '',
          unitPrice: null,
          currency: '',
        };
        setValue(nameofForm('declarationCharges'), [newDeclarationRow, ...(declarationCharges || [])]);
        break;
      }
      case 'container': {
        const newContainerRow = {
          chargeName: '',
          chargeCategory: '',
          containerType: '',
          legType: '',
          unitPrice20: null,
          unitPrice40: null,
          currency: '',
        };
        setValue(nameofForm('containerCharges'), [newContainerRow, ...(containerCharges || [])]);
        break;
      }
      case 'shipment': {
        const newShipmentRow = {
          chargeName: '',
          chargeCategory: '',
          legType: '',
          unitPrice: null,
          currency: '',
        };
        setValue(nameofForm('shipmentDeclarationCharges'), [newShipmentRow, ...(shipmentDeclarationCharges || [])]);
        break;
      }
      case 'weight': {
        const newWeightRow = {
          chargeName: '',
          chargeCategory: '',
          minimumChargeableWeight: null,
          maximumChargeableWeight: null,
          legType: '',
          unitPrice: null,
          minimumPrice: null,
          currency: '',
          chargeUnit: 'KG',
        };
        setValue(nameofForm('weightCharges'), [newWeightRow, ...(weightCharges || [])]);
        break;
      }
      case 'percentage': {
        const newPercentageRow = {
          chargeName: '',
          chargeCategory: '',
          legType: '',
          value: null,
          currency: '',
          minimumPrice: null,
        };
        setValue(nameofForm('percentageCharges'), [newPercentageRow, ...(percentageCharges || [])]);
        break;
      }
      default: {
        // Default to shipment if category is unknown
        const defaultRow = {
          chargeName: '',
          chargeCategory: '',
          legType: '',
          unitPrice: null,
          currency: '',
        };
        setValue(nameofForm('shipmentDeclarationCharges'), [defaultRow, ...(shipmentDeclarationCharges || [])]);
        break;
      }
    }

    // Trigger edit mode for the first row (index 0)
    setEditingRowIndex(0);
    setIsEditing(true);
  };

  const handleEditingChange = (editing: boolean) => {
    setIsEditing(editing);
  };

  return (
    <>
      {/* Show shipment details - same as previous steps */}
      {matchingService?.unmatchedService && (
        <div className='p-24 border-t-1 border-[#DFDFDF]'>
          <div>
            <h3 className='text-16 font-semibold mb-0 text-neutral-80'>Shipment Details</h3>
            <p className='text-[#475B69] text-14 xl:text-16 leading-[170%] font-normal mb-16'>Service details read from the shipment</p>
            <TableUnrecognizedService data={[matchingService.unmatchedService]} mismatchedFields={[]} />
          </div>
        </div>
      )}

      <div className='px-24 mt-24'>
        <NoContractSectionHeader
          title='Review, edit, add or remove Charges for your new Shipping Service'
          description='Amend the charges that were copied over as per your requirements and indicate the validity period'
        />

        <div className='mt-24'>
          <div className='flex items-center justify-between mb-16 gap-16'>
            {/* Left side: Date inputs */}
            <div className='flex gap-16 mb-16'>
              <StyledBox>
                <RhfSimpleDatePicker name='newMatchingService.existingShippingService.validityPeriod.start' placeholder='Start Date' />
              </StyledBox>
              <StyledBox>
                <RhfSimpleDatePicker name='newMatchingService.existingShippingService.validityPeriod.end' placeholder='End Date' />
              </StyledBox>
            </div>

            {/* Right side: Charge category dropdown and Add button */}
            <div className='flex items-center gap-12'>
              <StyledBox>
                <SelectDropdown
                  label='Charge Category'
                  options={chargeCategoryOptions}
                  value={chargeCategory}
                  setValue={(value: string) => setChargeCategory(value)}
                  urlParamName='category'
                />
              </StyledBox>
              <Button
                variant='contained'
                color='primary'
                startIcon={<AddIcon />}
                onClick={handleAddNewCharge}
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content',
                }}
              >
                Add New Charge
              </Button>
            </div>
          </div>

          {chargeCategory === 'declaration' && (
            <TableChargesDeclaration
              data={declarationCharges || []}
              enableEditing={isEditable}
              editingRowIndex={editingRowIndex}
              onEditingRowChange={setEditingRowIndex}
              onEditingChange={handleEditingChange}
            />
          )}

          {chargeCategory === 'container' && (
            <TableChargesContainer
              data={containerCharges || []}
              enableEditing={isEditable}
              editingRowIndex={editingRowIndex}
              onEditingRowChange={setEditingRowIndex}
              onEditingChange={handleEditingChange}
            />
          )}

          {chargeCategory === 'shipment' && (
            <TableChargesShipment
              data={shipmentDeclarationCharges || []}
              enableEditing={isEditable}
              editingRowIndex={editingRowIndex}
              onEditingRowChange={setEditingRowIndex}
              onEditingChange={handleEditingChange}
            />
          )}

          {chargeCategory === 'weight' && (
            <TableChargesWeight
              data={weightCharges || []}
              enableEditing={isEditable}
              editingRowIndex={editingRowIndex}
              onEditingRowChange={setEditingRowIndex}
              onEditingChange={handleEditingChange}
            />
          )}

          {chargeCategory === 'percentage' && (
            <TableChargesPercentage
              data={percentageCharges || []}
              enableEditing={isEditable}
              editingRowIndex={editingRowIndex}
              onEditingRowChange={setEditingRowIndex}
              onEditingChange={handleEditingChange}
            />
          )}
        </div>
      </div>
    </>
  );
};
